import type { SceneElement, Vec2, Vec3 } from "@calcplan/domain";

/**
 * The closed physical volume currently drawn for an element, expressed in the
 * element root's local axes and already including its transform scale.
 *
 * `dimensionsMm` remains the parametric envelope supplied to SketchUp dynamic
 * formulas. Those two boxes are deliberately not interchangeable: a basculant
 * door can make the imported/parametric depth 550 mm while the closed cabinet
 * body that must meet the wall occupies only 365.6 mm.
 */
export interface RenderedElementEnvelope {
  minimumMm: Vec3;
  maximumMm: Vec3;
  elementRevision: number;
}

export interface RenderedElementPlanGeometry {
  centerMm: Vec2;
  widthMm: number;
  depthMm: number;
}

export interface RenderedElementEnvelopeState {
  envelope: RenderedElementEnvelope;
  source: "measured" | "parametric_fallback";
  status: "ready" | "pending";
  elementRevision: number;
  measuredRevision: number | null;
  closedPose: true;
}

const envelopes = new Map<string, RenderedElementEnvelope>();
const listeners = new Set<() => void>();
let version = 0;

export function publishRenderedElementEnvelope(
  element: SceneElement,
  envelope: Pick<RenderedElementEnvelope, "minimumMm" | "maximumMm">,
): void {
  const next: RenderedElementEnvelope = {
    minimumMm: finiteBounds(envelope.minimumMm, true),
    maximumMm: finiteBounds(envelope.maximumMm, false),
    elementRevision: element.revision,
  };
  const previous = envelopes.get(element.id);
  if (previous && envelopeEquals(previous, next)) return;
  envelopes.set(element.id, next);
  emitChange();
}

export function clearRenderedElementEnvelope(elementId: string): void {
  if (!envelopes.delete(elementId)) return;
  emitChange();
}

export function pruneRenderedElementEnvelopes(elementIds: ReadonlySet<string>): void {
  let changed = false;
  for (const elementId of envelopes.keys()) {
    if (elementIds.has(elementId)) continue;
    envelopes.delete(elementId);
    changed = true;
  }
  if (changed) emitChange();
}

export function subscribeRenderedElementEnvelopes(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getRenderedElementEnvelopesVersion(): number {
  return version;
}

/** Uses the measured closed volume when it still belongs to this scene revision. */
export function renderedElementEnvelope(element: SceneElement): RenderedElementEnvelope {
  return renderedElementEnvelopeState(element).envelope;
}

/**
 * Describes which geometric authority produced the envelope.
 *
 * Dynamic modules are rendered asynchronously. Callers must not mistake the
 * parametric fallback for a measured physical body while Babylon is still
 * publishing the current element revision.
 */
export function renderedElementEnvelopeState(element: SceneElement): RenderedElementEnvelopeState {
  const measured = envelopes.get(element.id);
  if (measured && measured.elementRevision === element.revision) {
    return {
      envelope: measured,
      source: "measured",
      status: "ready",
      elementRevision: element.revision,
      measuredRevision: measured.elementRevision,
      closedPose: true,
    };
  }
  const halfWidth = Math.abs(element.dimensionsMm.width * element.transform.scale[0]) / 2;
  const halfHeight = Math.abs(element.dimensionsMm.height * element.transform.scale[1]) / 2;
  const halfDepth = Math.abs(element.dimensionsMm.depth * element.transform.scale[2]) / 2;
  return {
    envelope: {
      minimumMm: [-halfWidth, -halfHeight, -halfDepth],
      maximumMm: [halfWidth, halfHeight, halfDepth],
      elementRevision: element.revision,
    },
    source: "parametric_fallback",
    status: "pending",
    elementRevision: element.revision,
    measuredRevision: measured?.elementRevision ?? null,
    closedPose: true,
  };
}

/** Waits until Babylon publishes the closed physical body for this revision. */
export async function waitForRenderedElementEnvelope(
  element: SceneElement,
  timeoutMs = 8_000,
): Promise<RenderedElementEnvelopeState> {
  const initial = renderedElementEnvelopeState(element);
  if (initial.status === "ready") return initial;
  return await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      listeners.delete(onChange);
      window.clearTimeout(timeout);
      resolve(renderedElementEnvelopeState(element));
    };
    const onChange = () => {
      if (renderedElementEnvelopeState(element).status === "ready") finish();
    };
    listeners.add(onChange);
    const timeout = window.setTimeout(finish, Math.max(0, timeoutMs));
    // Avoid missing a publication between the initial read and subscription.
    onChange();
  });
}

export function renderedElementSizeMm(element: SceneElement): Vec3 {
  const bounds = renderedElementEnvelope(element);
  return [
    Math.max(1, bounds.maximumMm[0] - bounds.minimumMm[0]),
    Math.max(1, bounds.maximumMm[1] - bounds.minimumMm[1]),
    Math.max(1, bounds.maximumMm[2] - bounds.minimumMm[2]),
  ];
}

/** Centre of the closed physical volume in world millimetres. */
export function renderedElementCenterMm(element: SceneElement): Vec3 {
  const bounds = renderedElementEnvelope(element);
  const local: Vec3 = [
    (bounds.minimumMm[0] + bounds.maximumMm[0]) / 2,
    (bounds.minimumMm[1] + bounds.maximumMm[1]) / 2,
    (bounds.minimumMm[2] + bounds.maximumMm[2]) / 2,
  ];
  const yaw = finite(element.transform.rotationRad[1]);
  const cos = Math.cos(yaw);
  const sin = Math.sin(yaw);
  return [
    element.transform.translationMm[0] + local[0] * cos + local[2] * sin,
    element.transform.translationMm[1] + local[1],
    element.transform.translationMm[2] - local[0] * sin + local[2] * cos,
  ];
}

export function renderedElementPlanGeometry(element: SceneElement): RenderedElementPlanGeometry {
  const center = renderedElementCenterMm(element);
  const size = renderedElementSizeMm(element);
  return {
    centerMm: [center[0], center[2]],
    widthMm: size[0],
    depthMm: size[2],
  };
}

export function renderedElementVerticalIntervalMm(element: SceneElement): [number, number] {
  const bounds = renderedElementEnvelope(element);
  return [
    element.transform.translationMm[1] + bounds.minimumMm[1],
    element.transform.translationMm[1] + bounds.maximumMm[1],
  ];
}

function emitChange(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

function finiteBounds(value: Vec3, minimum: boolean): Vec3 {
  return value.map((entry) => {
    if (Number.isFinite(entry)) return Math.round(entry * 10) / 10;
    return minimum ? -0.5 : 0.5;
  }) as Vec3;
}

function envelopeEquals(left: RenderedElementEnvelope, right: RenderedElementEnvelope): boolean {
  return left.elementRevision === right.elementRevision
    && left.minimumMm.every((value, index) => Math.abs(value - right.minimumMm[index]!) < 0.05)
    && left.maximumMm.every((value, index) => Math.abs(value - right.maximumMm[index]!) < 0.05);
}

function finite(value: number): number {
  return Number.isFinite(value) ? value : 0;
}
